// The feature-chat turn lifecycle: question → answer → history (ORP-20465).
//
// Headless and panel-scoped. Feature chat is a short conversation ABOUT one record, so its state
// lives with the panel rather than in `orpheusStore` (which owns the full-page assistant's threads,
// streaming and thread mutations — none of which apply here).
import { computed, getCurrentScope, onScopeDispose, ref, shallowRef } from 'vue'

import { orpheusApi, type OrpheusApi } from '../api/OrpheusApi'
import type { FeatureChatMessageDto } from '../models/featureChat.dto'
import type {
  FeatureChatMessage,
  FeatureChatMode,
  FeatureChatTargetRef,
} from '../models/featureChat.types'
import { orpheusKeyError } from '../service/orpheusApiKey'
import { orpheusConsentRequired } from '../service/orpheusConsent'
import { useOrpheusConsentStore } from '../stores/orpheusConsentStore'
import {
  answeredAssistantMessage,
  buildFeatureChatAskBody,
  failedAssistantMessage,
  mapHistory,
  pendingAssistantMessage,
  userMessage,
} from '../service/featureChat'
import { classifyFeatureChatError } from '../service/featureChatError'

import { useWorkspace } from '@/features/account'
import { useCreditGuard } from '@/features/credits'

export interface FeatureChatContext {
  featureKey: string
  targetRef?: FeatureChatTargetRef | null | undefined
  focusRef?: string | null | undefined
  mode?: FeatureChatMode | undefined
}

export interface UseFeatureChatOptions {
  api?: Pick<
    OrpheusApi,
    'askFeatureChat' | 'getFeatureChatHistory' | 'getFeatureCatalog' | 'getFeatureSetupSession'
  >
}

/** A half-built setup found on open, waiting for "continue" or "start over". */
interface PendingSetupSession {
  sessionId: string
  messages: FeatureChatMessageDto[]
}

/**
 * Sessions already opened in this browser session, keyed by workspace + record.
 *
 * Reopening the panel on a record you have already asked about restores the transcript through
 * `GET …/sessions/<uuid>/history` instead of asking again — which would bill a second credit for
 * an answer the user has already read. v3 had no such memory and re-asked on every open.
 * Module-level so it survives the panel unmounting; process-lifetime only, never persisted.
 */
const sessionsByRecord = new Map<string, string>()

function recordKey(workspaceId: unknown, context: FeatureChatContext): string {
  return JSON.stringify([
    String(workspaceId),
    context.featureKey,
    context.targetRef ?? null,
    context.focusRef ?? '',
  ])
}

let turnSequence = 0
function nextLocalId(role: string): string {
  turnSequence += 1
  return `feature-chat:${role}:${turnSequence}`
}

export function useFeatureChat(options: UseFeatureChatOptions = {}) {
  const api = options.api ?? orpheusApi
  const { workspaceId } = useWorkspace()
  const credits = useCreditGuard()

  const messages = ref<FeatureChatMessage[]>([])
  const busy = ref(false)
  const sessionId = shallowRef<string | null>(null)
  /** Setup mode: the catalog's suggested openings, shown before anything is asked. */
  const suggestions = ref<string[]>([])
  const pendingSession = shallowRef<PendingSetupSession | null>(null)
  /** The next ask starts over: the backend archives the half-built setup first. */
  let startNewSession = false
  let controller: AbortController | null = null
  /**
   * The record the panel is currently pointed at. Held here rather than taken at construction so
   * one panel instance can follow the host's selection without being torn down and rebuilt;
   * `open()` is the only writer.
   */
  let context: FeatureChatContext = { featureKey: '' }

  function abort(): void {
    controller?.abort()
    controller = null
  }

  if (getCurrentScope()) onScopeDispose(abort)

  function reset(): void {
    abort()
    messages.value = []
    sessionId.value = null
    busy.value = false
    suggestions.value = []
    pendingSession.value = null
    startNewSession = false
  }

  function replaceAssistant(localId: string, next: FeatureChatMessage): void {
    const index = messages.value.findIndex((message) => message.localId === localId)
    if (index === -1) return
    messages.value.splice(index, 1, next)
  }

  async function ask(question: string): Promise<void> {
    const text = question.trim()
    const workspace = workspaceId.value
    if (!text || busy.value || workspace === undefined) return
    // Typing past the resume offer is a new request: the old turns were never shown, so it cannot
    // be an answer to them. Continuing takes the button.
    if (pendingSession.value) startOver()

    const assistantLocalId = nextLocalId('assistant')
    messages.value.push(
      userMessage(nextLocalId('user'), text),
      pendingAssistantMessage(assistantLocalId),
    )
    busy.value = true
    abort()
    controller = new AbortController()

    try {
      const dto = await api.askFeatureChat(
        workspace,
        buildFeatureChatAskBody({
          featureKey: context.featureKey,
          question: text,
          targetRef: context.targetRef,
          focusRef: context.focusRef,
          sessionId: sessionId.value,
          mode: context.mode,
          newSession: startNewSession,
        }),
        { signal: controller.signal },
      )

      const answered = answeredAssistantMessage(assistantLocalId, dto)
      replaceAssistant(assistantLocalId, answered)

      if (dto.session?.id) {
        sessionId.value = dto.session.id
        startNewSession = false
        if (context.mode !== 'setup')
          sessionsByRecord.set(recordKey(workspace, context), dto.session.id)
      }
      // The turn was billed server-side; refresh so the topbar pill stops showing a stale balance.
      if (!answered.error) void credits.refreshBalance()
    } catch (error) {
      // A 402 hydrates the credit store straight from the error's balance payload, so the panel's
      // depleted state and the topbar agree without a second call. `handleDepletion` owns that;
      // the classification below owns which surface the panel renders.
      credits.handleDepletion(error)
      const consentOff = orpheusConsentRequired(error)
      // The account turned Orpheus off: the panel swaps to its closed state on this answer.
      // Resolved here, not at setup: a refusal is the only path that needs the shared answer.
      if (consentOff) useOrpheusConsentStore().markConsentRequired(consentOff.accountAdmins)
      // The account's own key failed. The ask endpoint doesn't take `use_orphex_credits`, so the
      // card here offers everything but the one-question Orphex-credits answer.
      const keyError = orpheusKeyError(error)
      replaceAssistant(
        assistantLocalId,
        keyError
          ? {
              ...pendingAssistantMessage(assistantLocalId),
              pending: false,
              keyError: { ...keyError, replayable: false },
            }
          : failedAssistantMessage(assistantLocalId, classifyFeatureChatError(error)),
      )
    } finally {
      busy.value = false
      controller = null
    }
  }

  /** Restore an existing session's transcript. Returns false when there is nothing to restore. */
  async function restore(): Promise<boolean> {
    const workspace = workspaceId.value
    if (workspace === undefined) return false
    const known = sessionsByRecord.get(recordKey(workspace, context))
    if (!known) return false

    busy.value = true
    abort()
    controller = new AbortController()
    try {
      const dto = await api.getFeatureChatHistory(workspace, known, undefined, {
        signal: controller.signal,
      })
      const restored = mapHistory(dto, (row) => `feature-chat:history:${row.id}`)
      if (restored.length === 0) return false
      messages.value = restored
      sessionId.value = known
      return true
    } catch {
      // A transcript we cannot read is not an error worth showing: the caller falls through to
      // asking, which produces the same answer at the cost of one credit.
      sessionsByRecord.delete(recordKey(workspace, context))
      return false
    } finally {
      busy.value = false
      controller = null
    }
  }

  /** Open the panel on a record: restore the prior conversation, or ask the opening question. */
  async function open(next: FeatureChatContext, openingQuestion: string): Promise<void> {
    reset()
    context = next
    if (await restore()) return
    await ask(openingQuestion)
  }

  /**
   * Open a setup surface without a charged turn (KUR-9): the backend's own suggestions start it,
   * and a half-built setup is offered back before anything is asked. Opened with what a form
   * already held (`fromForm`), it is a new setup — never the old one.
   */
  async function openSetup(featureKey: string, fromForm = false): Promise<void> {
    reset()
    context = { featureKey, mode: 'setup' }
    const workspace = workspaceId.value
    if (workspace === undefined) return
    const [catalog, open] = await Promise.allSettled([
      api.getFeatureCatalog(workspace),
      api.getFeatureSetupSession(workspace, featureKey),
    ])
    if (context.featureKey !== featureKey) return
    if (catalog.status === 'fulfilled') {
      const feature = catalog.value.features?.find((item) => item.feature_key === featureKey)
      suggestions.value = (feature?.suggested_questions ?? []).slice(0, 3)
    }
    if (open.status === 'fulfilled') {
      const session = open.value.session
      const rows = Array.isArray(open.value.messages) ? open.value.messages : []
      if (session?.id && rows.length) {
        if (fromForm) startNewSession = true
        else pendingSession.value = { sessionId: session.id, messages: rows }
      }
    }
  }

  function continueSession(): void {
    const pending = pendingSession.value
    pendingSession.value = null
    if (!pending) return
    sessionId.value = pending.sessionId
    messages.value = mapHistory(
      { messages: pending.messages },
      (row) => `feature-chat:history:${row.id}`,
    )
  }

  function startOver(): void {
    pendingSession.value = null
    startNewSession = true
  }

  return {
    messages: computed(() => messages.value),
    busy: computed(() => busy.value),
    sessionId: computed(() => sessionId.value),
    suggestions: computed(() => suggestions.value),
    pendingSession: computed(() => pendingSession.value !== null),
    ask,
    open,
    openSetup,
    continueSession,
    startOver,
    reset,
  }
}
